import constants from './Constants';
import settings from './Settings';

const texturesDir = `${settings.imgDir}textures/planets/`

// Year lengths from https://nssdc.gsfc.nasa.gov/planetary/factsheet/planet_table_ratio.html

const planetsData = {
  Mercury: {
    name: "Mercury",
    labelScaleCoefficient: 0.15,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 1516 * constants.LENGTH_MILE,
    orbitRadius: 0.387 * constants.LENGTH_AU,
    axialTiltDeg: 0.3,
    earthBasedOrbitalPeriod: 0.241,
    rotationPeriodInSeconds: 5067031,
    rotationDirectionSameEarth: true,
    materialMap: texturesDir+"mercurymap.jpg",
    materialBumpMap: texturesDir+"mercurybump.jpg",
    materialBumpScale: 0.001,
    minCameraDistance: 1516 * constants.LENGTH_MILE * 6.5,
    description: "Mercury is a rocky planet and the closest planet in the Solar System to our sun. Its year is only 88 earth days and it is tidally locked, always facing the sun. Its temperature varies 600°C celsius between night and day.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Mercury_in_color_-_Prockter07-edit1.jpg/440px-Mercury_in_color_-_Prockter07-edit1.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Mercury_(planet)",
  },
  Venus: {
    name: "Venus",
    labelScaleCoefficient: 0.15,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 3760 * constants.LENGTH_MILE,
    orbitRadius: 0.723 * constants.LENGTH_AU,
    axialTiltDeg: 2.64,
    earthBasedOrbitalPeriod: 0.615,
    rotationPeriodInSeconds: 20996815,
    rotationDirectionSameEarth: false,
    materialMap: texturesDir+"venusmap.jpg",
    materialBumpMap: texturesDir+"venusbump.jpg",
    materialBumpScale: 0.01,
    minCameraDistance: 3760 * constants.LENGTH_MILE * 5,
    description: "While Venus is further from the sun than Mercury, its mean surface temperature of 462°C is much hotter. This is caused by its dense atmosphere, 92 times that of Earth, which has created a massive greenhouse effect. Many of its other characteristics, however, are similar to those of our homeworld.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/Venus-real_color.jpg/440px-Venus-real_color.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Venus",
  },
  Earth: {
    name: "Earth",
    labelScaleCoefficient: 0.8,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: constants.UD/2,
    orbitRadius: constants.LENGTH_AU,
    axialTiltDeg: 23.44,
    earthBasedOrbitalPeriod: 1,
    rotationPeriodInSeconds: 86164,
    rotationDirectionSameEarth: true,
    materialMap: texturesDir+"earthmap.jpg",
    materialBumpMap: texturesDir+"earthbump.jpg",
    materialBumpScale: 0.01,
    minCameraDistance: constants.UD * 2,
    description: "Earth is the home of humanity and the only place in the universe known to harbor life. This is allowed by a great number of factors, such as the right distance from the sun and plentiful liquid water. Life is theorized to have started 4.1 billion years ago when the planet was only 400 million years old. Over time, it diversified into countless species. Very recently, humans have risen to the top and have spread across the globe.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/97/The_Earth_seen_from_Apollo_17.jpg/440px-The_Earth_seen_from_Apollo_17.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Earth",
  },
  Mars: {
    name: "Mars",
    labelScaleCoefficient: .3,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 2106 * constants.LENGTH_MILE,
    orbitRadius: 1.524 * constants.LENGTH_AU,
    axialTiltDeg: 25.19,
    earthBasedOrbitalPeriod: 1.88,
    rotationPeriodInSeconds: 88642,
    rotationDirectionSameEarth: true,
    materialMap: texturesDir+"marsmap.jpg",
    materialBumpMap: texturesDir+"marsbump.jpg",
    materialBumpScale: 0.01,
    minCameraDistance:  2106 * constants.LENGTH_MILE * 5,
    description: "Mars is a rocky planet smaller than Earth. Iron oxide gives it a red color. Its geology shows signs that it previously harbored liquid water on its surface before it lost most of its atmosphere, and therefore had potential for life. Because of this, there have been many missions to the red planet.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/02/OSIRIS_Mars_true_color.jpg/440px-OSIRIS_Mars_true_color.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Mars",
  },
  Jupiter: {
    name: "Jupiter",
    labelScaleCoefficient: 5,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 43441 * constants.LENGTH_MILE,
    orbitRadius: 5.203 * constants.LENGTH_AU,
    axialTiltDeg: 3.13,
    earthBasedOrbitalPeriod: 11.9,
    rotationPeriodInSeconds: 35430,
    rotationDirectionSameEarth: true,
    materialMap: texturesDir+"jupitermap.jpg",
    minCameraDistance: 43441 * constants.LENGTH_MILE * 5,
    description: "Jupiter is a gas giant with a mass 2.5 times that of all other planets in the solar system combined. Its turbulent atmosphere is filled with storms. One of these, the Great Red Spot, could fit Earth within it, and has been raging since at least 1831. Jupiter has many moons. One moon, Europa, is believed to hold a vast water ocean under its icy crust.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/Jupiter%2C_image_taken_by_NASA%27s_Hubble_Space_Telescope%2C_June_2019_-_Edited.jpg/440px-Jupiter%2C_image_taken_by_NASA%27s_Hubble_Space_Telescope%2C_June_2019_-_Edited.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Jupiter",
  },
  Saturn: {
    name: "Saturn",
    labelScaleCoefficient: 5,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 36184 * constants.LENGTH_MILE,
    orbitRadius: 9.539 * constants.LENGTH_AU,
    axialTiltDeg: 26.73,
    earthBasedOrbitalPeriod: 29.4,
    rotationPeriodInSeconds: 38364,
    rotationDirectionSameEarth: true,
    materialMap: texturesDir+"saturnmap.jpg",
    minCameraDistance: 36184 * constants.LENGTH_MILE * 5,
    description: "Saturn is a gas giant further and smaller than Jupiter. Its huge rings are made of rocks and dust. Among its many moons are Enceladus, thought to have a deep subsurface water ocean, and Titan, the only moon in the Solar System with a substantial atmosphere.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c7/Saturn_during_Equinox.jpg/440px-Saturn_during_Equinox.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Saturn",
  },
  Uranus: {
    name: "Uranus",
    labelScaleCoefficient: 3,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 15759 * constants.LENGTH_MILE,
    orbitRadius: 19.18 * constants.LENGTH_AU,
    axialTiltDeg: 82.23,
    earthBasedOrbitalPeriod: 83.7,
    rotationPeriodInSeconds: 62063,
    rotationDirectionSameEarth: false,
    materialMap: texturesDir+"uranusmap.jpg",
    minCameraDistance: 15759 * constants.LENGTH_MILE * 5,
    description: "Uranus is a frigid ice giant. Its atmospheric temperature drops as low as −224°C, making it the coldest in the Solar System. The poles of Uranus are tilted about 90° to where the equator is on other planets.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3d/Uranus2.jpg/440px-Uranus2.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Uranus",
  },
  Neptune: {
    name: "Neptune",
    labelScaleCoefficient: 3,
    labelMaxDistance: constants.LENGTH_AU * 1000,
    radius: 15299 * constants.LENGTH_MILE,
    orbitRadius: 30.06 * constants.LENGTH_AU,
    axialTiltDeg: 28.32,
    earthBasedOrbitalPeriod: 163.7,
    rotationPeriodInSeconds: 57996,
    rotationDirectionSameEarth: true,
    materialMap: texturesDir+"neptunemap.jpg",
    minCameraDistance: 15299 * constants.LENGTH_MILE * 5,
    description: "Neptune, the most distant planet in the Solar System, is a cold ice giant slightly more massive than Uranus. It is invisible without visual aid, and was first observed in 1846. Neptune has the fastest winds of all the eight planets, reaching speeds up to 580 meters per second.",
    descriptionImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/63/Neptune_-_Voyager_2_%2829347980845%29_flatten_crop.jpg/440px-Neptune_-_Voyager_2_%2829347980845%29_flatten_crop.jpg",
    descriptionSource: "https://en.wikipedia.org/wiki/Neptune",
  },
}

export default planetsData;